import { useIntl } from 'react-intl';

import { WooZip } from 'utils/type-guard/woo-zip';
import { formatBytes } from 'utils/format-bytes';
import { NextLink } from 'components/next-link';
import { Icon } from 'components/icon/icon';
import { Typography } from 'components/typography';
import Image from 'components/image';

import IcnCarotRight from 'public/assets/icons/carot-right.svg';

import styles from './card.module.scss';

export interface ICardCoverImage {
	url: string;
	alternativeText?: string;
	formats?: Record<string, any>;
}

export interface ICardProps {
	title: string;
	slug: string;
	bodyText?: React.JSX.Element;
	cover?: ICardCoverImage;
	priority?: boolean;
	type?: string;
	date?: Date;
	file?: WooZip;
}

export const Card = ({ title, slug, bodyText, cover, priority, type, date, file }: ICardProps): React.JSX.Element => {
	const { formatDate } = useIntl();

	const dateLabel = date ? formatDate(date) : undefined;
	const fileSize = file?.size ? formatBytes(file.size) : undefined;
	const fileFormat = file?.ext ? `(${file.ext})` : undefined;

	return (
		<NextLink href={slug} passHref>
			<div key={title} className={styles.card}>
				<div className={styles.main}>
					<span className={styles.articleTitle}>
						<span className={styles.icon}>
							<Icon customIcon={<IcnCarotRight />} size="12px" />
						</span>
						<Typography tag="h2" variant="h5">
							<span className={styles.title}>{title}</span>
						</Typography>
					</span>

					{bodyText && <>{bodyText}</>}

					{(type || dateLabel || fileSize) && (
						<div className={styles.contentFooter}>
							{type && <div>{type}</div>}

							{(fileSize || dateLabel) && (
								<div className={styles.contentRight}>
									{fileSize && fileFormat && (
										<>
											<div>
												{fileSize} {fileFormat}
											</div>
											<div>|</div>
										</>
									)}
									{dateLabel && <div>{dateLabel}</div>}
								</div>
							)}
						</div>
					)}
				</div>

				{cover?.url && (
					<div className={styles.image}>
						<Image
							attributes={cover}
							style={{ objectFit: 'cover' }}
							sizes="(max-width: 768px) 720px, (max-width: 1200px) 570px, 390px"
							priority={priority}
							imageSize="medium"
							fill
						/>
					</div>
				)}
			</div>
		</NextLink>
	);
};

export default Card;
